import React, { useEffect, useState, useRef, useLayoutEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Wine,
  TrendingUp,
  Star,
  BarChart3,
  ChevronRight,
  Loader2,
  Milk,
  MapPin,
  Users,
  UserCheck,
} from 'lucide-react';
import { motion, useMotionValue, useTransform, animate, useInView } from 'framer-motion';
import gsap from 'gsap';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';
import { useWines } from '@/contexts/WinesContext';
import { useAuth } from '@/contexts/AuthContext';
import { Layout } from '@/components/layout/Layout';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/Card';
import type { WineType } from '@/types';

const wineTypeLabels: Record<WineType, string> = {
  red: 'Rosso',
  white: 'Bianco',
  rose: 'Rosato',
  sparkling: 'Spumante',
  dessert: 'Dessert',
  fortified: 'Fortificato',
  orange: 'Arancione',
  whisky: 'Whisky',
  rum: 'Rum',
  gin: 'Gin',
  grappa: 'Grappa',
  cognac: 'Cognac',
  liqueur: 'Liquore',
  other: 'Altro',
};

// Colori allineati ai badge (vedi HomePage.tsx e WineDetailPage.tsx)
const wineTypeColors: Record<WineType, string> = {
  // Vini - Palette naturale e sofisticata
  red: '#980F2D',       // wine-burgundy
  white: '#FBBF24',     // amber-400 (match con bg-amber-100)
  rose: '#C07283',      // wine-rose
  sparkling: '#0EA5E9', // sky-500 (match con bg-sky-100)
  dessert: '#F59E0B',   // amber-500 (match con bg-amber-200)
  fortified: '#8B2E4F', // wine-merlot
  orange: '#FB923C',    // orange-400 (match con bg-orange-100)

  // Spirits - Palette terra e legno
  whisky: '#B87333',    // wine-amber
  rum: '#F59E0B',       // amber-500 (match con bg-amber-200)
  gin: '#10B981',       // emerald-500 (match con bg-emerald-100)
  grappa: '#78716C',    // stone-500 (match con bg-stone-200)
  cognac: '#8B7355',    // wine-oak
  liqueur: '#E879F9',   // fuchsia-400 (match con bg-fuchsia-100)

  other: '#9CA3AF',     // gray-400 (match con bg-gray-200)
};

export const StatsPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { wines, loading, getWineStats } = useWines();
  const chartContainerRef = useRef<HTMLDivElement>(null);
  const typeBreakdownRef = useRef<HTMLDivElement>(null);
  const [selectedType, setSelectedType] = useState<string | null>(null);


  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-wine-purple" />
        </div>
      </Layout>
    );
  }

  const stats = getWineStats();

  // Calculate total bottles in cellar
  const totalBottles = wines
    .filter((w) => w.status === 'cellar')
    .reduce((sum, w) => sum + (w.quantity || 1), 0);

  // Calculate shared bottles statistics
  const sharedByMeFullBottles = wines
    .filter((w) =>
      w.status === 'cellar' &&
      w.ownerId === user?.id &&
      w.isShared &&
      w.sharedWith?.some(share => share.type === 'full')
    )
    .reduce((sum, w) => sum + (w.quantity || 1), 0);

  const sharedByMeReadOnlyBottles = wines
    .filter((w) =>
      w.status === 'cellar' &&
      w.ownerId === user?.id &&
      w.isShared &&
      w.sharedWith?.some(share => share.type === 'read-only') &&
      !w.sharedWith?.some(share => share.type === 'full')
    )
    .reduce((sum, w) => sum + (w.quantity || 1), 0);

  const sharedWithMeBottles = wines
    .filter((w) =>
      w.status === 'cellar' &&
      w.ownerId !== user?.id &&
      w.sharedWithUserIds?.includes(user?.id || '')
    )
    .reduce((sum, w) => sum + (w.quantity || 1), 0);

  // Prepare chart data
  const chartData = Object.entries(stats.winesByType)
    .filter(([_, count]) => count > 0)
    .map(([type, count]) => ({
      name: wineTypeLabels[type as WineType],
      value: count,
      color: wineTypeColors[type as WineType],
    }));

  // Calculate distribution by region (only cellar wines)
  const regionDistribution: Record<string, number> = {};
  wines
    .filter((w) => w.status === 'cellar' && w.region)
    .forEach((wine) => {
      const region = wine.region!;
      regionDistribution[region] = (regionDistribution[region] || 0) + 1;
    });

  // Sort regions by count and get top regions
  const regionData = Object.entries(regionDistribution)
    .sort(([, a], [, b]) => b - a)
    .slice(0, 10) // Top 10 regions
    .map(([region, count]) => ({
      name: region,
      value: count,
    }));

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('it-IT', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  };

  const formatDate = (timestamp: any) => {
    if (!timestamp) return 'N/A';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return new Intl.DateTimeFormat('it-IT', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    }).format(date);
  };

  // GSAP Chart Animations
  useLayoutEffect(() => {
    if (!chartContainerRef.current || chartData.length === 0) return;

    const ctx = gsap.context(() => {
      // Animate chart container
      gsap.from(chartContainerRef.current, {
        opacity: 0,
        scale: 0.95,
        duration: 0.8,
        ease: 'power2.out',
        delay: 0.3,
      });
    });

    return () => ctx.revert();
  }, [chartData]);


  return (
    <Layout>
      <div className="space-y-4 sm:space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Statistiche</h1>
          <p className="text-sm sm:text-base text-gray-600 mt-1">Panoramica della tua cantina</p>
        </div>

        {/* KPI Cards */}
        <motion.div
          initial="hidden"
          animate="visible"
          variants={{
            hidden: { opacity: 0 },
            visible: {
              opacity: 1,
              transition: {
                staggerChildren: 0.1,
                delayChildren: 0.2,
              },
            },
          }}
          className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-4 sm:gap-6"
        >
          <StatCard
            icon={<Wine className="w-6 h-6" />}
            label="Vini registrati"
            value={stats.totalWines}
            color="bg-wine-purple"
          />
          <StatCard
            icon={<BarChart3 className="w-6 h-6" />}
            label="In Cantina"
            value={stats.cellarWines}
            color="bg-green-600"
          />
          <StatCard
            icon={<TrendingUp className="w-6 h-6" />}
            label="Valore Cantina"
            value={formatCurrency(stats.totalValue)}
            color="bg-blue-600"
          />
          <StatCard
            icon={<Milk className="w-6 h-6" />}
            label="Q.tà Bottiglie"
            value={totalBottles}
            color="bg-purple-600"
          />
          <StatCard
            icon={<Users className="w-6 h-6" />}
            label="Condivisi da Me"
            value={`${sharedByMeFullBottles} / ${sharedByMeReadOnlyBottles}`}
            color="bg-green-500"
          />
          <StatCard
            icon={<UserCheck className="w-6 h-6" />}
            label="Condivisi con Me"
            value={sharedWithMeBottles}
            color="bg-orange-500"
          />
        </motion.div>

        {/* Distribution Chart */}
        {chartData.length > 0 && (
          <ScrollReveal>
            <Card>
              <CardHeader>
                <CardTitle>Distribuzione per Tipo</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-col md:flex-row gap-6 md:gap-8">
                  {/* Grafico a Torta */}
                  <div className="w-full md:w-1/2">
                    <div ref={chartContainerRef} className="w-full relative">
                      <ResponsiveContainer width="100%" height={400}>
                        <PieChart>
                          <defs>
                            {chartData.map((entry, index) => (
                              <linearGradient
                                key={`gradient-${index}`}
                                id={`gradient-${index}`}
                                x1="0"
                                y1="0"
                                x2="1"
                                y2="1"
                              >
                                <stop offset="0%" stopColor={entry.color} stopOpacity={1} />
                                <stop offset="100%" stopColor={entry.color} stopOpacity={0.7} />
                              </linearGradient>
                            ))}
                          </defs>
                          <Pie
                            data={chartData}
                            cx="50%"
                            cy="50%"
                            innerRadius={80}
                            outerRadius={140}
                            paddingAngle={2}
                            dataKey="value"
                            animationBegin={0}
                            animationDuration={1000}
                          >
                            {chartData.map((entry, index) => (
                              <Cell
                                key={`cell-${index}`}
                                fill={`url(#gradient-${index})`}
                                stroke="#FFFFFF"
                                strokeWidth={3}
                                opacity={selectedType === null || selectedType === entry.name ? 1 : 0.2}
                                style={{ cursor: 'pointer' }}
                                onClick={() => setSelectedType(selectedType === entry.name ? null : entry.name)}
                              />
                            ))}
                          </Pie>
                          <Tooltip
                            contentStyle={{
                              backgroundColor: '#FFFFFF',
                              border: 'none',
                              borderRadius: '0.75rem',
                              boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
                              padding: '12px'
                            }}
                            itemStyle={{
                              color: '#6B7280',
                              fontSize: '14px',
                              fontWeight: '500'
                            }}
                            formatter={(value: any) => [`${value} vini`, 'Totale']}
                          />
                        </PieChart>
                      </ResponsiveContainer>

                      {/* Testo Centrale */}
                      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                        <div className="text-center">
                          <p className="text-4xl sm:text-5xl font-bold text-gray-900">
                            {selectedType
                              ? chartData.find(d => d.name === selectedType)?.value
                              : chartData.reduce((sum, item) => sum + item.value, 0)}
                          </p>
                          <p className="text-sm sm:text-base text-gray-500 mt-1">
                            {selectedType || 'Totale'}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Type Breakdown */}
                  <div ref={typeBreakdownRef} className="w-full md:w-1/2 grid grid-cols-2 gap-3 content-start">
                    {chartData.map((item) => {
                      const isSelected = selectedType === item.name;
                      const isOtherSelected = selectedType !== null && selectedType !== item.name;

                      return (
                        <motion.div
                          key={item.name}
                          className={`type-item flex items-center gap-3 p-4 rounded-xl border-2 cursor-pointer transition-all duration-300 ${
                            isSelected
                              ? 'bg-gradient-to-br from-wine-purple/10 to-wine-purple/5 border-wine-purple shadow-lg'
                              : isOtherSelected
                              ? 'bg-gradient-to-br from-gray-50 to-white border-gray-100 opacity-40'
                              : 'bg-gradient-to-br from-gray-50 to-white border-gray-100 hover:shadow-md'
                          }`}
                          whileHover={{ scale: isOtherSelected ? 1 : 1.03, y: isOtherSelected ? 0 : -2 }}
                          whileTap={{ scale: 0.98 }}
                          onClick={() => setSelectedType(selectedType === item.name ? null : item.name)}
                        >
                          <div
                            className="w-4 h-4 rounded-full shadow-sm flex-shrink-0"
                            style={{ backgroundColor: item.color }}
                          />
                          <div className="flex-1 min-w-0">
                            <p className={`text-xs truncate ${isSelected ? 'text-wine-purple font-medium' : 'text-gray-500'}`}>
                              {item.name}
                            </p>
                            <p className={`text-xl font-bold ${isSelected ? 'text-wine-purple' : 'text-gray-900'}`}>
                              {item.value}
                            </p>
                          </div>
                        </motion.div>
                      );
                    })}
                  </div>
                </div>
              </CardContent>
            </Card>
          </ScrollReveal>
        )}

        {/* Distribution by Region */}
        {regionData.length > 0 && (
          <ScrollReveal>
            <Card>
              <CardHeader>
                <CardTitle>Distribuzione per Regione</CardTitle>
              </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {regionData.map((item) => (
                  <div
                    key={item.name}
                    className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg"
                  >
                    <div className="w-10 h-10 bg-wine-purple/10 rounded-full flex items-center justify-center flex-shrink-0">
                      <MapPin className="w-5 h-5 text-wine-purple" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">{item.name}</p>
                      <p className="text-xs text-gray-500">
                        {item.value} {item.value === 1 ? 'vino' : 'vini'}
                      </p>
                    </div>
                    <div className="flex-shrink-0">
                      <p className="text-xl font-bold text-wine-purple">{item.value}</p>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
          </ScrollReveal>
        )}

        {/* Top Rated Wines */}
        {stats.topRatedWines.length > 0 && (
          <ScrollReveal>
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle>Vini Più Votati</CardTitle>
                <span className="text-sm text-gray-600">
                  {stats.topRatedWines.length} vini
                </span>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {stats.topRatedWines.map((wine) => (
                  <div
                    key={wine.id}
                    onClick={() => navigate(`/wine/${wine.id}`)}
                    className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg hover:bg-gray-100 cursor-pointer transition-colors"
                  >
                    {wine.imageUrl ? (
                      <img
                        src={wine.imageUrl}
                        alt={wine.name}
                        className="w-16 h-16 object-cover rounded-lg"
                      />
                    ) : (
                      <div className="w-16 h-16 bg-wine-purple/10 rounded-lg flex items-center justify-center">
                        <Wine className="w-8 h-8 text-wine-purple/30" />
                      </div>
                    )}
                    <div className="flex-1">
                      <h4 className="font-bold text-gray-900">{wine.name}</h4>
                      <p className="text-sm text-gray-600">{wine.producer}</p>
                      <div className="flex items-center gap-1 mt-1">
                        {Array.from({ length: 5 }).map((_, i) => (
                          <Star
                            key={i}
                            className={`w-4 h-4 ${
                              i < (wine.rating || 0)
                                ? 'text-yellow-400 fill-current'
                                : 'text-gray-300'
                            }`}
                          />
                        ))}
                      </div>
                    </div>
                    <ChevronRight className="w-5 h-5 text-gray-400" />
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
          </ScrollReveal>
        )}

        {/* Recently Added */}
        {stats.recentlyAdded.length > 0 && (
          <ScrollReveal>
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle>Aggiunti di Recente</CardTitle>
                <span className="text-sm text-gray-600">
                  Ultimi {stats.recentlyAdded.length}
                </span>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {stats.recentlyAdded.slice(0, 5).map((wine) => (
                  <div
                    key={wine.id}
                    onClick={() => navigate(`/wine/${wine.id}`)}
                    className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg hover:bg-gray-100 cursor-pointer transition-colors"
                  >
                    {wine.imageUrl ? (
                      <img
                        src={wine.imageUrl}
                        alt={wine.name}
                        className="w-16 h-16 object-cover rounded-lg"
                      />
                    ) : (
                      <div className="w-16 h-16 bg-wine-purple/10 rounded-lg flex items-center justify-center">
                        <Wine className="w-8 h-8 text-wine-purple/30" />
                      </div>
                    )}
                    <div className="flex-1">
                      <h4 className="font-bold text-gray-900">{wine.name}</h4>
                      <p className="text-sm text-gray-600">{wine.producer}</p>
                      <p className="text-xs text-gray-500 mt-1">
                        Aggiunto il {formatDate(wine.createdAt)}
                      </p>
                    </div>
                    <ChevronRight className="w-5 h-5 text-gray-400" />
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
          </ScrollReveal>
        )}


        {/* Empty State */}
        {wines.length === 0 && (
          <Card padding="lg">
            <div className="text-center py-12">
              <BarChart3 className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                Nessuna statistica disponibile
              </h3>
              <p className="text-gray-600">
                Aggiungi vini alla tua cantina per vedere le statistiche!
              </p>
            </div>
          </Card>
        )}
      </div>
    </Layout>
  );
};

interface ScrollRevealProps {
  children: React.ReactNode;
}

const ScrollReveal: React.FC<ScrollRevealProps> = ({ children }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.2 });

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 50 }}
      animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 50 }}
      transition={{ duration: 0.6, ease: 'easeOut' }}
    >
      {children}
    </motion.div>
  );
};

interface CounterProps {
  value: number;
  duration?: number;
}

const Counter: React.FC<CounterProps> = ({ value, duration = 1.5 }) => {
  const count = useMotionValue(0);
  const rounded = useTransform(count, (latest) => Math.round(latest));
  const [displayValue, setDisplayValue] = useState(0);

  useEffect(() => {
    const controls = animate(count, value, {
      duration,
      ease: 'easeOut',
    });

    return controls.stop;
  }, [value, duration, count]);

  useEffect(() => {
    const unsubscribe = rounded.on('change', (latest) => {
      setDisplayValue(latest);
    });

    return () => unsubscribe();
  }, [rounded]);

  return <>{displayValue}</>;
};

interface StatCardProps {
  icon: React.ReactNode;
  label: string;
  value: string | number;
  color: string;
}

const StatCard: React.FC<StatCardProps> = ({ icon, label, value, color }) => {
  const isNumeric = typeof value === 'number';
  const isCurrency = typeof value === 'string' && value.startsWith('€');

  return (
    <motion.div
      variants={{
        hidden: { opacity: 0, y: 20 },
        visible: {
          opacity: 1,
          y: 0,
          transition: {
            duration: 0.5,
            ease: 'easeOut',
          },
        },
      }}
    >
      <Card padding="md">
        <div className="flex flex-col sm:flex-row items-center gap-3 sm:gap-4">
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ duration: 0.5, delay: 0.2, type: 'spring', stiffness: 200 }}
            className={`p-3 rounded-lg ${color} text-white`}
          >
            {icon}
          </motion.div>
          <div className="text-center sm:text-left">
            <p className="text-xs sm:text-sm text-gray-600">{label}</p>
            <p className="text-xl sm:text-2xl font-bold text-gray-900 break-words">
              {isNumeric ? (
                <Counter value={value} />
              ) : isCurrency ? (
                value
              ) : (
                value
              )}
            </p>
          </div>
        </div>
      </Card>
    </motion.div>
  );
};
